import config from '../../config';

const apiUrl = config.apiUrl;

export interface Protocol {
  protocolId: number;
  protocolNumber: string;
  teacherName: string;
  subjectName: string;
  specialityName: string;
  examTypeName: string;
  sessionTypeName: string;
  issueDate: string;
  examDate: string;
  academicYearName: string;
  semester: string;
  isSigned: boolean;
}

export interface ProtocolTeacherResponse {
  teacherId: number;
  teacherFullName: string;
  teacherName: string;
}

export interface SubjectResponse {
  subjectId: number;
  name: string;
  nameEn: string | null;
  specialityId?: number | null;
  specialityName: string | null;
  specialityNameEn: string | null;
  minPassedExams?: number | null;
  parentId?: number | null;
  parentName: string | null;
  children?: SubjectResponse[];
}

export interface ProtocolStudentGradeComponentResponse {
  studentGradeComponentId?: number;
  studentGradeId?: number;
  subjectId: number;
  subjectName?: string | null;
  grade: number;
  gradeName?: string | null;
  updateDate?: string | null;
  modifiedByUsername?: string | null;
}

export interface ProtocolResponse {
  protocolId: number;
  protocolNumber: string;
  teacherName: string;
  subjectName: string;
  specialityName: string;
  examDate: string;
  academicYearName: string;
  semesterTypeName: string;
  examTypeId: number;
  examTypeName?: string;
  sessionTypeName?: string;
  isSigned: boolean;
  protocolTeachers?: ProtocolTeacherResponse[];
  childSubjects: SubjectResponse[];
}

export interface ProtocolGradeResponse {
  studentGradeId: number;
  protocolId: number;
  studentId: number;
  facultyNumber?: string;
  studentName?: string;
  studentNameEn?: string;
  groupName?: string;
  grade: number;
  gradeName?: string;
  gradeECTS?: string;
  studentGradeTypeId: number;
  studentGradeTypeName: string;
  studentGradeTypeNameEn?: string;
  updateDate?: Date;
  modifiedByUsername?: string;
  attestation?: boolean | null;
  thesisTitle?: string;
  subjectId?: number;
  semester?: number;
  subjectName?: string;
  studentGradeComponents?: ProtocolStudentGradeComponentResponse[];
}

export interface ProtocolGradeRequest {
  StudentGradeId: number;
  StudentId: number;
  ProtocolId: number;
  Grade: number;
  StudentGradeTypeId: number;
  StudentGradeComponents: ProtocolStudentGradeComponentResponse[];
  Attestation: boolean | null;
  ThesisTitle?: string;
  UpdateDate?: Date;
}

export interface RecognitionProtocolResponse {
  protocolId?: number;
  protocolNumber: string;
  teacherId: number;
  teacherName: string;
  teacherNameEn?: string;
  substituteTeacherId?: number;
  specialityId: number;
  specialityName: string;
  specialityNameEn?: string;
  studentId: number;
  facultyNumber?: string;
  studentName?: string;
  studentNameEn?: string;
  issueDate?: string;
  examDate?: string;
  examTypeId?: number;
  examTypeName?: string;
  examTypeNameEn?: string;
  academicYearId?: number;
  academicYearName?: string;
  currentVersion?: number;
  currentStatus?: string;
  isSigned: boolean;
  signedBy?: string;
  signedOn?: string;
  protocolTeachers?: ProtocolTeacherTeamResponse[];
}

export interface ProtocolTeacherTeamResponse {
  teacherId: number;
  teacherFullName: string;
  teacherName: string;
}

const getAuthHeader = () => {
  const accessToken = localStorage.getItem('accessToken');
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };
};

interface ProtocolQueryParams {
  fn?: string;
  studentName?: string;
  archive?: boolean;
  myDepartment?: boolean;
  showUnassigned?: boolean;
}

export const protocolsService = {
  async getProtocols(params?: ProtocolQueryParams): Promise<Protocol[]> {
    const queryParams = new URLSearchParams();
    if (params?.fn) queryParams.append('fn', params.fn);
    if (params?.studentName) queryParams.append('studentName', params.studentName);
    if (params?.archive) queryParams.append('archive', params.archive.toString());
    if (params?.myDepartment) queryParams.append('myDepartment', params.myDepartment.toString());
    if (params?.showUnassigned) queryParams.append('showUnassigned', params.showUnassigned.toString());

    const response = await fetch(`${apiUrl}/Protocols/GetProtocols?${queryParams.toString()}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch protocols');
    }
    const data = await response.json();
    return data.data.sort((a: Protocol, b: Protocol) => 
      new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime()
    );
  },

  async signProtocols(protocolIds: number[]): Promise<void> {
    const response = await fetch(`${apiUrl}/Protocols/SignProtocols`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(protocolIds),
    });

    if (!response.ok) {
      const errorData = await response.json();
      // @ts-ignore
      window.Rollbar?.error('Failed to sign protocols', {
        errorMessage: errorData.message,
        errorData: errorData,
        timestamp: new Date().toISOString()
      });
      throw new Error('Failed to sign protocols');
    }
  },

  async checkProtocolsSignature(): Promise<boolean> {
    const response = await fetch(`${apiUrl}/Protocols/CheckProtocolsSignature`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to check protocols signatures');
    }

    const result = await response.json();
    return result.data;
  },

  async getProtocolById(id: string): Promise<ProtocolResponse> {
    const response = await fetch(`${apiUrl}/Protocols/${id}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch protocol details');
    }

    const data = await response.json();
    return data.data;
  },

  async getProtocolGrades(id: string): Promise<ProtocolGradeResponse[]> {
    const response = await fetch(`${apiUrl}/Protocols/GetProtocolGrades/${id}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch protocol grades');
    }

    const data = await response.json();
    return data.data || [];
  },

  async updateStudentGrade(gradeRequest: ProtocolGradeRequest): Promise<void> {
    const response = await fetch(`${apiUrl}/Protocols/UpdateStudentGrade`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(gradeRequest),
    });

    if (!response.ok) {
      const errorData = await response.json();
      // @ts-ignore
      window.Rollbar?.error('Failed to update grade', {
        errorMessage: errorData.message,
        errorData: errorData,
        timestamp: new Date().toISOString()
      });
      throw new Error(errorData.message || 'Failed to update grade');
    }
  },

  async downloadWorkingProtocol(id: string, format: 'pdf' | 'excel'): Promise<Blob> {
    const response = await fetch(`${apiUrl}/Protocols/DownloadWorkingProtocol/${id}?format=${format}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to export protocol as ${format}`);
    }

    return await response.blob();
  },

  async downloadSignedProtocol(protocolId: number): Promise<Blob> {
    const response = await fetch(`${apiUrl}/Protocols/DownloadSignedProtocol/${protocolId}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to download signed protocol');
    }

    return await response.blob();
  },

  async checkProtocolSignature(protocolId: number): Promise<boolean> {
    const response = await fetch(`${apiUrl}/Protocols/CheckProtocolSignature/${protocolId}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to check protocol signature');
    }

    const result = await response.json();
    return result.data;
  },

  async createProtocolVersion(protocolId: number): Promise<boolean> {
    const response = await fetch(`${apiUrl}/Protocols/CreateProtocolVersion/${protocolId}`, {
      method: 'POST',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      // @ts-ignore
      window.Rollbar?.error('Failed to create protocol version', {
        errorMessage: errorData.message,
        errorData: errorData,
        timestamp: new Date().toISOString()
      });
      throw new Error(errorData.message || 'Failed to create protocol version');
    }


    const result = await response.json();
    return result.data;
  },

  async updateProtocolExamDate(protocolId: number, examDate: string): Promise<void> {
    const response = await fetch(`${apiUrl}/Protocols/UpdateExamDate/${protocolId}?examDate=${examDate}`, {
      method: 'POST',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      // @ts-ignore
      window.Rollbar?.error('Failed to update exam date', {
        errorMessage: errorData.message,
        errorData: errorData,
        timestamp: new Date().toISOString()
      });
      throw new Error(errorData.message || 'Failed to update exam date');

    }
  },

  async getRecognitionProtocol(protocolId: number): Promise<RecognitionProtocolResponse> {
    const response = await fetch(`${apiUrl}/Protocols/recognition/${protocolId}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch recognition protocol');
    }

    const data = await response.json();
    return data.data;
  }
}; 